import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Bell, Cast, Monitor, Palette, Star, Zap } from "lucide-react";
import { Header } from "../public-landing/components/shared/Header";
import { Footer } from "../public-landing/components/shared/Footer";
import { CookieBanner } from "../public-landing/components/shared/CookieBanner";
import { FacebookPixel } from "../public-landing/components/shared/FacebookPixel";
import { GoogleAnalytics } from "../public-landing/components/shared/GoogleAnalytics";
import {
  LandingPrimaryImageCtaSection,
  LandingConicCtaBg,
  LandingFeatureList,
  LandingProductCardSection,
  LandingProductCard,
  LandingFaqCollapsibleSection,
} from "../public-landing/components/landing";
import { Button } from "../public-landing/components/ui/button";
import { trackShopifyAppStoreLead } from "../public-landing/lib/facebookPixel";
import { siteConfig } from "../public-landing/siteConfig";
import "../styles/public-reset.css";
import "../styles/public-landing.css";

type MeResponse = { shop?: string } | null;

const testimonials = [
  {
    quote:
      '"Magnify has been a really useful addition for keeping our team aligned on store performance. The preset wallboards already cover most of the key metrics we wanted to share internally, so we were up and running quickly without needing to build everything from scratch."',
    source: "The Home of Gifts",
  },
  {
    quote:
      '"...a clean and practical way to make sales, marketing, and inventory data more visible and actionable for the whole team."',
    source: "The Home of Gifts",
  },
];

export default function PublicLanding() {
  const [me, setMe] = useState<MeResponse>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await fetch("/api/me", { credentials: "include" });
        if (!res.ok) throw new Error("not ok");
        const data = await res.json();
        if (isMounted) setMe(data);
      } catch {
        if (isMounted) setMe(null);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const isAuthenticatedShop = Boolean(me?.shop);

  return (
    <div
      id="public-landing"
      className="dark fancy-overlay bg-white text-black antialiased min-h-screen flex flex-col"
    >
      <Header className="mb-4" />

      <main className="flex-1 w-full flex flex-col items-center">
        <LandingPrimaryImageCtaSection
          titleComponent={
            <h1 className="font-semibold text-4xl md:text-5xl lg:text-6xl leading-tight md:max-w-3xl">
              {"Your Shopify Store, "}
              <span className="font-semibold bg-gradient-to-r from-primary-300 via-secondary-500 to-primary-300 bg-clip-text text-transparent">
                Always in Sight{" "}
              </span>
            </h1>
          }
          description={siteConfig.description}
          imageSrc="/landing/static/images/office-preview.png"
          imageAlt="Dashboard Wallboard"
          imagePosition="right"
          imageShadow="hard"
          textPosition="left"
          variant="primary"
          withBackground={false}
          withBackgroundGlow
          backgroundGlowVariant="primary"
          effectComponent={<LandingConicCtaBg variant="primary" />}
        >
          {isAuthenticatedShop ? (
            <Button size="xl" asChild>
              <Link to="/app/wallboards">Go to my wallboards</Link>
            </Button>
          ) : (
            <Button size="xl" asChild>
              <a
                href={siteConfig.appStoreUrl}
                onClick={trackShopifyAppStoreLead}
              >
                View on Shopify App Store
              </a>
            </Button>
          )}
          <Button size="xl" variant="outlinePrimary" asChild>
            <a href="#features">{"Features"}</a>
          </Button>
        </LandingPrimaryImageCtaSection>

        <LandingFeatureList
          id="features"
          className="scroll-mt-10"
          title="Everything You Need for Your Wallboard"
          description="Powerful features to keep your team informed and motivated with real-time store metrics."
          featureItems={[
            {
              title: "Live Sales Updates",
              description:
                "See sales as they happen with real-time notifications and updates on your big screen display.",
              icon: <Monitor className="w-8 h-8" />,
            },
            {
              title: "Order Notifications",
              description:
                "Get instant order alerts with customer details and order information displayed prominently.",
              icon: <Bell className="w-8 h-8" />,
            },
            {
              title: "Customizable Cards",
              description:
                "Tailor your dashboard with metrics that matter most to your team and business goals.",
              icon: <Star className="w-8 h-8" />,
            },
            {
              title: "Visual Themes",
              description:
                "Match your brand aesthetic with customizable themes and color schemes for your wallboard.",
              icon: <Palette className="w-8 h-8" />,
            },
            {
              title: "Easy Setup",
              description:
                "Get started in minutes with our simple installation process and intuitive configuration.",
              icon: <Zap className="w-8 h-8" />,
            },
            {
              title: "Multi-Screen Support",
              description:
                "Display your dashboard across multiple screens and locations simultaneously.",
              icon: <Cast className="w-8 h-8" />,
            },
          ]}
          withBackground
          withBackgroundGlow
          variant="primary"
          backgroundGlowVariant="primary"
        />

        <LandingProductCardSection
          id="whos-it-for"
          className="scroll-mt-10"
          title="Perfect for Every Team"
          description="MagnifyBI adapts to your team's needs, whether you're in the warehouse, sales room, or marketing office."
          withBackground={false}
          withBackgroundGlow={false}
          variant="primary"
          backgroundGlowVariant="primary"
          textPosition="center"
        >
          <LandingProductCard
            title="Sales Rooms"
            description="Motivate your sales team with real-time performance metrics and celebrate wins as they happen."
            imageSrc="/landing/static/images/photo-1542744173-8e7e53415bb0.jpg"
            imageAlt="Sales Room"
            variant="primary"
          />
          <LandingProductCard
            title="Marketing Dashboards"
            description="Track the impact of your marketing campaigns with conversion rates and revenue metrics displayed live."
            imageSrc="/landing/static/images/photo-1557804506-669a67965ba0.jpg"
            imageAlt="Marketing Dashboard"
            variant="primary"
          />
          <LandingProductCard
            title="Warehouse Teams"
            description="Keep your warehouse informed about orders and shipping with real-time updates on fulfillment status."
            imageSrc="/landing/static/images/photo-1553413077-190dd305871c.jpg"
            imageAlt="Warehouse Team"
            variant="primary"
          />
        </LandingProductCardSection>

        <LandingFaqCollapsibleSection
          id="faq"
          className="scroll-mt-10"
          title="Frequently Asked Questions"
          description="Everything you need to know about MagnifyBI and how it works with your Shopify store."
          faqItems={[
            {
              question: "Is my data secure?",
              answer:
                "Yes, we use industry-standard security measures to protect your data. All connections are encrypted with SSL, and we never store sensitive payment information. Your Shopify data is accessed through secure OAuth authentication.",
            },
            {
              question: "How easy is it to set up?",
              answer:
                "It only takes a few minutes to install and connect your store. Simply install the app from the Shopify App Store, authorize the connection, and you're ready to cast your dashboard to any screen.",
            },
            {
              question: "What devices can I use to display the dashboard?",
              answer:
                "You can display MagnifyBI wallboards on any device with a web browser, including smart TVs, tablets, computers, and dedicated display screens. We support Chromecast and AirPlay for wireless casting.",
            },
            {
              question: "Can I customize what metrics are displayed?",
              answer:
                "Absolutely! You have full control over which widgets and metrics appear on your dashboard. Choose from sales, orders, revenue, top products, and many more customizable widgets.",
            },
            {
              question: "Can I change the theme and colors?",
              answer:
                "Yes, MagnifyBI offers multiple visual themes and you can customize colors to match your brand identity and office environment.",
            },
            {
              question: "How often does the data update?",
              answer:
                "Data updates in real-time as orders come in. You'll see new sales and orders appear on your dashboard within seconds of them happening in your Shopify store.",
            },
            {
              question: "What kind of support do you offer?",
              answer:
                "We provide email support for all users, with priority support available for premium plans. Our documentation and video tutorials are also available 24/7.",
            },
          ]}
          withBackground
          withBackgroundGlow={false}
          variant="primary"
          backgroundGlowVariant="primary"
        />

        <section className="w-full py-12 lg:py-16">
          <div className="container-wide mx-auto w-full px-6 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight">
              What Shopify teams say
            </h2>
            <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-2">
              {testimonials.map((testimonial) => (
                <article
                  key={testimonial.quote}
                  className="relative overflow-hidden rounded-xl border border-primary-100/20 bg-primary-100/10 px-6 pb-6 pt-14 text-left shadow-sm"
                >
                  <div
                    aria-hidden="true"
                    className="absolute left-5 top-3 font-display text-7xl font-semibold leading-none text-primary-300/40"
                  >
                    &ldquo;
                  </div>
                  <p className="relative text-lg leading-8 text-gray-100">
                    {testimonial.quote}
                  </p>
                  <div className="mt-6 flex items-center gap-1 text-primary-300">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        className="h-5 w-5 fill-current"
                        aria-hidden="true"
                      />
                    ))}
                    <span className="sr-only">Five stars</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-white">
                    <i>from</i> {testimonial.source}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer className="mt-8" />
      <GoogleAnalytics />
      <FacebookPixel />
      <CookieBanner />
    </div>
  );
}
